import FilterList from '@mui/icons-material/FilterList';
import { paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { debounce } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { CapacityBreakdownRow } from '~hooks/useCapacityBreakdown/models';
import useCapacityBreakdownQuery, {
  CAPACITY_BREAKDOWN_PAGE_SIZE,
} from '~queries/useCapacityBreakdownQuery';
import useEquipmentTypesInfiniteQuery from '~queries/useEquipmentTypesInfiniteQuery';
import { PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getPageNumber } from '~utils/pagination';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const EQUIPMENT_TYPES_QUERY_KEY = 'CapacityBreakdown_equipmentTypes';

interface PageRequest {
  companyId?: string;
  cursor?: string;
  link?: PaginationLink;
  page: number;
}

const FIRST_PAGE_REQUEST: PageRequest = { page: 1 };

// The endpoint already groups rows by vendor, so the grid only reorders each contiguous
// vendor run and never reorders the runs themselves.
function sortWithinVendorGroups(rows: CapacityBreakdownRow[]) {
  const groups: CapacityBreakdownRow[][] = [];

  rows.forEach((row) => {
    const currentGroup = groups.at(-1);

    if (currentGroup && currentGroup[0].vendor.id === row.vendor.id) {
      currentGroup.push(row);
    } else {
      groups.push([row]);
    }
  });

  return groups.flatMap((group) =>
    group.sort((a, b) => a.equipmentType.name.localeCompare(b.equipmentType.name)),
  );
}

const CapacityBreakdown = observer(() => {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [selectedEquipmentType, setSelectedEquipmentType] =
    useState<Nullable<FilterMenuOption>>(null);
  const [searchQuery, setSearchQuery] = useState({ input: '', debounced: '' });
  const [gridSearchValue, setGridSearchValue] = useState('');
  const [requestedPage, setRequestedPage] = useState(FIRST_PAGE_REQUEST);
  const lastLoadedPageRef = useRef(FIRST_PAGE_REQUEST);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

  const equipmentTypesQuery = useEquipmentTypesInfiniteQuery(
    EQUIPMENT_TYPES_QUERY_KEY,
    companyId,
  );
  // A cursor only means something in the company that issued it.
  const pageRequest =
    requestedPage.companyId === undefined || requestedPage.companyId === companyId
      ? requestedPage
      : FIRST_PAGE_REQUEST;
  const { isError, isFetching, isFirstPage, pagination, rows } =
    useCapacityBreakdownQuery({
      companyId,
      cursor: pageRequest.cursor,
      equipmentTypeId: selectedEquipmentType?.value,
      link: pageRequest.link,
      search: gridSearchValue,
    });

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const columns = useMemo<Array<DataTableColumn<CapacityBreakdownRow>>>(
    () => [
      {
        field: 'vendor',
        headerName: t('form_fields.vendor'),
        valueGetter: (row) => row.vendor.name,
      },
      {
        field: 'equipmentType',
        headerName: t('form_fields.equipment_type'),
        valueGetter: (row) => row.equipmentType.name,
      },
      {
        field: 'committedCount',
        headerName: t('administration.vendors.committed_trucks'),
        valueGetter: (row) => row.committedCount,
      },
      {
        field: 'assignedCount',
        headerName: t('administration.vendors.assigned_trucks'),
        valueGetter: (row) => row.assignedCount,
      },
    ],
    [],
  );

  const groupedRows = useMemo(() => sortWithinVendorGroups(rows), [rows]);

  const handleSelectedOptionsChange = useCallback(
    (_options: string[], selectedOption: Nullable<string>) => {
      setSelectedEquipmentType(
        equipmentTypesQuery.data.find(({ value }) => value === selectedOption) ?? null,
      );
      setRequestedPage(FIRST_PAGE_REQUEST);
    },
    [equipmentTypesQuery.data],
  );

  const handleSearchChange = useCallback((searchValue: string) => {
    setGridSearchValue(searchValue);
    setRequestedPage(FIRST_PAGE_REQUEST);
  }, []);

  const handleNavigate = useCallback(
    (link?: PaginationLink) => {
      const cursor = link ? pagination?.[link] : undefined;

      if (!link || !cursor) {
        setRequestedPage(FIRST_PAGE_REQUEST);

        return;
      }

      setRequestedPage({
        companyId,
        cursor,
        link,
        page: getPageNumber(pageRequest.page, link),
      });
    },
    [companyId, pageRequest.page, pagination],
  );

  useEffect(() => {
    if (isError) {
      setRequestedPage(lastLoadedPageRef.current);
    } else if (!isFetching) {
      lastLoadedPageRef.current = pageRequest;
    }
  }, [isError, isFetching, pageRequest]);

  useEffect(() => {
    if (isFilterMenuOpen) {
      equipmentTypesQuery.fetch(searchQuery.debounced);
    }
  }, [equipmentTypesQuery.fetch, isFilterMenuOpen, searchQuery.debounced]);

  useEffect(() => {
    if (!isFilterMenuOpen) {
      setSearchQuery({ input: '', debounced: '' });
      equipmentTypesQuery.clear();
    }
  }, [isFilterMenuOpen]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mr: 3 }}>
      <Box sx={{ display: 'flex' }}>
        <Menu
          onOpenStateChanged={setIsFilterMenuOpen}
          sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
          menuTrigger={
            <SmallButton
              color="secondary"
              size="small"
              startIcon={<FilterList />}
              variant="outlined"
            >
              {selectedEquipmentType?.label ?? t('form_fields.equipment_type')}
            </SmallButton>
          }
        >
          <FilterMenuItem
            focusSearchFieldOnMount
            loadingReason={equipmentTypesQuery.fetchReason}
            onFetchMore={equipmentTypesQuery.fetchMore}
            onSearchValueChange={(value) =>
              setSearchQuery((state) => ({ ...state, input: value }))
            }
            onSelectedOptionsChange={handleSelectedOptionsChange}
            options={equipmentTypesQuery.data}
            searchValue={searchQuery.input}
            selectAllOptionLabel={t('dispatch.dispatch_v2.filters.all_entities', {
              entity: t('form_fields.equipment_type'),
            })}
            selectedOptions={selectedEquipmentType ? [selectedEquipmentType.value] : []}
          />
        </Menu>
      </Box>
      <DataTable
        id="vendor-capacity-breakdown-grid"
        columns={columns}
        rows={groupedRows}
        loading={isFetching}
        onSearchChange={handleSearchChange}
        noRowsOverlayText={t(
          selectedEquipmentType
            ? 'administration.vendors.capacity_breakdown_empty_filtered'
            : 'administration.vendors.capacity_breakdown_empty',
        )}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            onNavigate={handleNavigate}
            pagination={{
              ...pagination,
              limit: CAPACITY_BREAKDOWN_PAGE_SIZE,
              page: isFirstPage ? 1 : pageRequest.page,
            }}
          />
        }
      />
    </Box>
  );
});

export default CapacityBreakdown;
